/**
 * Simple but secure token generator that produces different tokens each time
 * @param key The secret key
 * @returns A time-limited token
 */
export function generateShortLivedToken(key: string): string {
  // Use a timestamp for expiration
  const timestamp: number = Math.floor(Date.now() / 1000);
  const expiryTime: number = timestamp + 60; // 1 minute validity

  // Add a nonce to ensure uniqueness
  const nonce: string = generateNonce();

  // Create a signature
  const data = `${expiryTime}:${nonce}`;
  const signature: string = createSignature(key, data);

  return `${expiryTime}:${nonce}:${signature}`;
}

/**
 * Verifies a token generated by generateShortLivedToken
 * @param token The token to verify
 * @param key The secret key used to generate the token
 * @returns true if the token is valid and not expired, false otherwise
 */
export function verifyShortLivedToken(token: string, key: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // Split the token into its components
  const parts: string[] = token.split(':');
  if (parts.length !== 3) {
    return false;
  }

  const expiryTimeStr = parts[0];
  const nonce = parts[1];
  const signature = parts[2];

  if (!expiryTimeStr || !nonce || !signature) {
    return false;
  }

  // Check if the token is expired
  const currentTime: number = Math.floor(Date.now() / 1000);
  const expiryTime: number = parseInt(expiryTimeStr, 10);

  if (isNaN(expiryTime) || expiryTime < currentTime) {
    return false;
  }

  // Verify the signature
  const data = `${expiryTimeStr}:${nonce}`;
  const expectedSignature: string = createSignature(key, data);

  return signature === expectedSignature;
}

/**
 * Generates a random nonce
 * @returns A random string
 */
function generateNonce(): string {
  // Generate a random number and convert to base36 (alphanumeric)
  return (
    Math.random().toString(36).substring(2, 10) +
    Math.random().toString(36).substring(2, 10)
  );
}

/**
 * Creates a signature for the given data using the key
 * @param key The secret key
 * @param data The data to sign
 * @returns A signature string
 */
function createSignature(key: string, data: string): string {
  let hash = 0;
  const str = key + data;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }

  // Add another layer of hashing with different parameters
  let hash2 = 5381;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash2 = (hash2 << 5) + hash2 + char;
    hash2 |= 0;
  }

  // Combine hashes and convert to hex
  return ((hash ^ hash2) >>> 0).toString(16).padStart(8, '0');
}
